// =============================================================================
// APP - Головний компонент застосунку
// =============================================================================

import { useState, useCallback, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LandingScreen,
  GameScreen,
  ErrorBoundary,
  ShareDonationScreen,
  SharePetitionScreen,
  CreateGamePage,
  GameRulesPage,
  GameMenu,
} from './components';
import { pageTransition } from './utils/animations';
import { getCampaignBySlug } from './services/api';
import { trackPageView, trackCampaignLoad } from './services/analytics';
import { STORAGE_KEYS } from './constants';

type GamePhase =
  | 'landing'
  | 'game'
  | 'share'
  | 'share-petition'
  | 'create-game'
  | 'rules';

/**
 * Extract campaign slug from subdomain
 * e.g., "my-org.holovkom.zavoruha.tech" -> "my-org"
 */
function extractCampaignSlug(): string | null {
  const host = window.location.host;

  // Match pattern: {slug}.holovkom.zavoruha.tech
  const match = host.match(
    /^([a-z0-9][a-z0-9-]*[a-z0-9])\.holovkom\.zavoruha\.tech$/i,
  );
  if (match) {
    return match[1].toLowerCase();
  }

  // For local development: {slug}.holovkom.localhost
  const localMatch = host.match(
    /^([a-z0-9][a-z0-9-]*[a-z0-9])\.holovkom\.localhost/i,
  );
  if (localMatch) {
    return localMatch[1].toLowerCase();
  }

  return null;
}

function App() {
  const [phase, setPhase] = useState<GamePhase>(() => {
    // Always start on landing page - game can be resumed from there
    return 'landing';
  });

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Check if there's saved game progress
  const hasSavedGame = () => {
    const saved = localStorage.getItem(STORAGE_KEYS.GAME_PROGRESS);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.gameState && parsed.gameState.phase === 'playing';
      } catch {
        return false;
      }
    }
    return false;
  };

  useEffect(() => {
    // Extract subdomain to detect campaign
    const campaignSlug = extractCampaignSlug();

    if (campaignSlug) {
      // Track campaign load
      trackCampaignLoad('subdomain', campaignSlug);

      // Fetch campaign data by slug
      getCampaignBySlug(campaignSlug)
        .then((campaign) => {
          if (campaign) {
            localStorage.setItem(
              STORAGE_KEYS.DONATION_CAMPAIGN,
              JSON.stringify(campaign),
            );
          }
        })
        .catch((e) => {
          console.error('Failed to load campaign by slug', e);
        })
        .finally(() => {
          setPhase('landing');
        });
      return;
    }

    // Handle other routes
    const path = window.location.pathname;

    if (path === '/share-donation') {
      setPhase('share');
    }

    if (path === '/share-petition') {
      setPhase('share-petition');
    }
  }, []);

  // Track page views on phase changes
  useEffect(() => {
    trackPageView(phase === 'share-petition' ? 'share-petition' : phase);
  }, [phase]);

  const handleStartGame = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.GAME_PROGRESS);
    setPhase('game');
  }, []);

  const handleContinueGame = useCallback(() => {
    setPhase('game');
  }, []);

  const handleGoToLanding = useCallback(() => {
    setPhase('landing');
  }, []);

  const handleNewGame = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.GAME_PROGRESS);
    setPhase('landing');
  }, []);

  const handleBackToGame = useCallback(() => {
    window.location.href = '/';
  }, []);

  const handleGoToCreateGame = useCallback(() => {
    setPhase('create-game');
  }, []);

  const handleGoToRules = useCallback(() => {
    setPhase('rules');
  }, []);

  const handleBackToLanding = useCallback(() => {
    setPhase('landing');
  }, []);

  const handleOpenMenu = useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-war-bg text-war-primary antialiased relative overflow-hidden font-mono">
        {/* Global tactical background */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(16,185,129,0.05)_0%,_transparent_70%)]" />
          <div className="scanlines" />
        </div>

        <AnimatePresence mode="wait">
          {phase === 'landing' && (
            <motion.div
              key="landing"
              variants={pageTransition}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <LandingScreen
                onStartGame={handleStartGame}
                onContinueGame={handleContinueGame}
                onCreateGame={handleGoToCreateGame}
                onViewRules={handleGoToRules}
                hasSavedGame={hasSavedGame()}
              />
            </motion.div>
          )}

          {phase === 'create-game' && (
            <motion.div
              key="create-game"
              variants={pageTransition}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <CreateGamePage onBack={handleBackToLanding} />
            </motion.div>
          )}

          {phase === 'rules' && (
            <motion.div
              key="rules"
              variants={pageTransition}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <GameRulesPage onBack={handleBackToLanding} />
            </motion.div>
          )}

          {phase === 'share' && (
            <motion.div
              key="share"
              variants={pageTransition}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <ShareDonationScreen onBack={handleBackToGame} />
            </motion.div>
          )}

          {phase === 'share-petition' && (
            <motion.div
              key="share-petition"
              variants={pageTransition}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <SharePetitionScreen onBack={handleBackToGame} />
            </motion.div>
          )}

          {phase === 'game' && (
            <motion.div
              key="game"
              variants={pageTransition}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <GameScreen
                onOpenMenu={handleOpenMenu}
                onNewGame={handleNewGame}
                onViewRules={handleGoToRules}
                onCreateGame={handleGoToCreateGame}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Game Menu */}
        <GameMenu
          isOpen={isMenuOpen}
          onClose={handleCloseMenu}
          onGoHome={handleGoToLanding}
          onViewRules={handleGoToRules}
          onCreateGame={handleGoToCreateGame}
          isInGame={phase === 'game'}
        />
      </div>
    </ErrorBoundary>
  );
}

export default App;
